import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Send, Sparkles, Link2, RotateCw, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { sendAtlasMessage } from "@/api/endpoints";
import { AtlasMessage, ApiError } from "@/types/api";
import { useI18n } from "@/i18n";

interface Props {
  matchContext?: { fixture_id?: string; label?: string };
  compact?: boolean;
}

export function AtlasAssistantPanel({ matchContext, compact }: Props) {
  const { d, lang } = useI18n();
  const [messages, setMessages] = useState<AtlasMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  async function submit(text: string) {
    const q = text.trim();
    if (!q || sending) return;
    setError(null);
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q, ts: Date.now() }]);
    setSending(true);
    try {
      const res = await sendAtlasMessage(q, matchContext?.fixture_id ? { fixture_id: matchContext.fixture_id } : undefined);
      const reply = res.reply || res.message || res.text
        || (lang === "tr" ? "ATLAS şu anda bir yanıt üretemedi. Lütfen tekrar deneyin." : "ATLAS couldn't produce a response right now. Please try again.");
      setMessages((m) => [...m, { role: "atlas", content: String(reply), ts: Date.now() }]);
    } catch (e) {
      setError(e as ApiError);
    } finally {
      setSending(false);
    }
  }

  const notConnected = error?.kind === "unauthorized" || error?.kind === "forbidden";
  const quotaExceeded = error?.kind === "quota";

  return (
    <div className={`panel flex flex-col overflow-hidden ${compact ? "h-[420px]" : "h-[calc(100vh-190px)] min-h-[460px]"}`} data-testid="atlas-panel">
      <div className="flex items-center gap-3 border-b border-line px-4 py-3">
        <span className="relative grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-atlas-500/90 to-atlas-700 shadow-glow">
          <Sparkles className="h-4.5 w-4.5 text-atlas-ink" />
          <span className="absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full border-2 border-navy-850 bg-atlas animate-live-pulse" />
        </span>
        <div>
          <p className="text-sm font-bold text-foreground">{d.atlas.title}</p>
          <p className="text-[11px] text-muted-foreground">{matchContext?.label || d.atlas.subtitle}</p>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {messages.length === 0 && !notConnected && !quotaExceeded && (
          <div className="flex h-full flex-col items-center justify-center gap-4 text-center">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-atlas/10 text-atlas-400">
              <Sparkles className="h-7 w-7" />
            </span>
            <div>
              <p className="text-base font-semibold text-foreground">{d.atlas.title}</p>
              <p className="mt-1 max-w-xs text-sm text-muted-foreground">{d.atlas.subtitle}</p>
            </div>
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`atlas-msg-${m.role}`}
            >
              <div
                className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${
                  m.role === "user"
                    ? "bg-atlas/15 text-foreground rounded-br-sm"
                    : "border border-line bg-white/[0.03] text-foreground/90 rounded-bl-sm"
                }`}
              >
                {m.content}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {sending && (
          <div className="flex justify-start">
            <div className="flex items-center gap-1.5 rounded-2xl rounded-bl-sm border border-line bg-white/[0.03] px-4 py-3">
              {[0, 1, 2].map((i) => (
                <span key={i} className="h-1.5 w-1.5 rounded-full bg-atlas-400 animate-live-pulse" style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}

        {notConnected && (
          <div className="flex h-full flex-col items-center justify-center gap-4 text-center" data-testid="atlas-connect-state">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gold/10 text-gold-400">
              <Link2 className="h-7 w-7" />
            </span>
            <div>
              <p className="text-base font-semibold text-foreground">{d.atlas.connectTitle}</p>
              <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{d.atlas.connectBody}</p>
            </div>
            <Link to="/login" data-testid="atlas-login-cta" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-atlas-500 to-atlas-600 px-5 py-2.5 text-sm font-bold text-atlas-ink transition hover:shadow-glow">
              {d.atlas.connectCta}
            </Link>
          </div>
        )}

        {quotaExceeded && (
          <div className="flex h-full flex-col items-center justify-center gap-4 text-center" data-testid="atlas-quota-state">
            <span className="grid h-14 w-14 place-items-center rounded-2xl bg-gold/10 text-gold-400">
              <Sparkles className="h-7 w-7" />
            </span>
            <div>
              <p className="text-base font-semibold text-foreground">{lang === "tr" ? "ATLAS Asistan planınıza dahil değil" : "ATLAS Assistant isn't included in your plan"}</p>
              <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{lang === "tr" ? "Bu özelliği kullanmak için üyeliğini yükselt." : "Upgrade your membership to use this feature."}</p>
            </div>
            <Link to="/app/membership" data-testid="atlas-upgrade-cta" className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-5 py-2.5 text-sm font-bold text-gold-300 transition hover:bg-gold/20">
              {lang === "tr" ? "Üyeliği görüntüle" : "View membership"}
            </Link>
          </div>
        )}

        {error && !notConnected && !quotaExceeded && (
          <div className="flex items-center justify-center gap-2 rounded-xl border border-live/25 bg-live/10 px-4 py-3 text-sm text-live-400" data-testid="atlas-error">
            <AlertTriangle className="h-4 w-4" /> {d.states.unavailable}
            <button onClick={() => messages.length && submit(messages[messages.length - 1].content)} className="ml-2 inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline">
              <RotateCw className="h-3.5 w-3.5" /> {d.common.retry}
            </button>
          </div>
        )}
      </div>

      {messages.length === 0 && !notConnected && !quotaExceeded && (
        <div className="flex flex-wrap gap-2 border-t border-line px-4 py-3">
          {d.atlas.suggestions.map((s) => (
            <button
              key={s}
              onClick={() => submit(s)}
              data-testid="atlas-suggestion"
              className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-muted-foreground transition hover:border-atlas/30 hover:text-atlas-300"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); submit(input); }}
        className="flex items-center gap-2 border-t border-line p-3"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={d.atlas.placeholder}
          data-testid="atlas-input"
          className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-atlas/40"
        />
        <button
          type="submit"
          disabled={!input.trim() || sending}
          data-testid="atlas-send-button"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-atlas-500 to-atlas-600 text-atlas-ink transition hover:shadow-glow disabled:opacity-40"
        >
          <Send className="h-4.5 w-4.5" />
        </button>
      </form>
    </div>
  );
}
